import { useContext, useState } from 'react'
import DotsLoader from 'shared/components/loaders/dots'
import UserContext from 'shared/contexts/user'
import styled from 'styled-components'
import { PortalAlertContext, PortalMessageContextManager } from 'ui'
import { ALL_ANIMATED_MIME_TYPES, VIDEO_MIME_TYPES } from '../../constants'
import { CreationToolsContext } from '../../manager'
import CreationToolOutputType from '../../types/output-type'
import CreationToolStep from '../../types/steps'
import CreationToolWelcomeButton from '../../types/welcome-button'
import { ColumnBlock, ContainerBlock, SplitBlock as CreationSplitBlock } from '../blocks'
import Breadcrumbs from '../breadcrumbs'
import Navigation from '../navigation'
import { Text, Title as TitleComponent } from '../typography'
import CreationToolsWelcomeFileButton from '../welcome/buttons/button/file'
import AddMoreButton from './add-more-button'
import Buttons from './buttons'
import CopilotDropdownComponent from './copilot/dropdown'
import CopilotContextManager from './copilot/manager'
import CopilotSidebar from './copilot/sidebar'
import Editor from './editor'
import Fields from './fields'
import List from './list'
import CreationToolsUploadManager from './manager'
import Preview from './preview'
import Terms from './terms'

const STEP = CreationToolStep.Upload

const Container = styled(ContainerBlock)`
    min-height: calc(100vh - 100px);
`

const SplitBlock = styled(CreationSplitBlock)`
    > div:last-child {
        padding-bottom: 0;
    }
`

const Header = styled.div`
    align-items: center;
    display: flex;
    margin-bottom: 20px;
`

const Title = styled(TitleComponent)`
    margin: 0;
`

const CopilotDropdown = styled(CopilotDropdownComponent)`
    flex-shrink: 0;
    margin-left: auto;
`

type Props = {
    submitUrl: string
    apiKey?: string
}

const CreationToolsUpload = ({ submitUrl, apiKey }: Props) => {
    const { sources, stepFrames, outputType } = useContext(CreationToolsContext)
    const { user } = useContext(UserContext)
    const isBulk = sources.length > 1
    const [isProcessing, setIsProcessing] = useState<boolean>(!stepFrames.length && isBulk)
    const description = CreationToolStep.description(STEP)
    // TODO is there a better way to do set this?
    const addMoreMimeTypes = outputType === CreationToolOutputType.Video ? VIDEO_MIME_TYPES : ALL_ANIMATED_MIME_TYPES

    return (
        <PortalMessageContextManager>
            <PortalAlertContext>
                <CreationToolsUploadManager submitUrl={submitUrl} apiKey={apiKey}>
                    <CopilotContextManager disabled={!user.isStaff}>
                        <Container>
                            <SplitBlock
                                style={isProcessing ? { opacity: 0, pointerEvents: 'none', maxHeight: '80vh' } : {}}
                            >
                                <ColumnBlock>
                                    <div>
                                        <Navigation>
                                            <CreationToolsWelcomeFileButton
                                                button={CreationToolWelcomeButton.UploadAddMore}
                                                mimeTypesOverride={addMoreMimeTypes}
                                            >
                                                <AddMoreButton isProcessing={isProcessing} />
                                            </CreationToolsWelcomeFileButton>
                                        </Navigation>
                                        {/*
                                            If source is not animated, and has step frames, go to preview. If not, goes to List.
                                            The big difference between both, is that <List /> allow to use UGE, Preview, not, and 
                                            we should be able to use UGE for animated sources.
                                        */}
                                        {!sources[0]?.isAnimated && stepFrames.length > 0 ? (
                                            <Preview />
                                        ) : (
                                            <List setIsProcessing={setIsProcessing} />
                                        )}
                                    </div>
                                    <Breadcrumbs />
                                </ColumnBlock>
                                <ColumnBlock>
                                    <div>
                                        <Header>
                                            <Title>{CreationToolStep.title(STEP)}</Title>
                                            <CopilotDropdown size="small" />
                                        </Header>
                                        {description && <Text>{description}</Text>}
                                        <Fields />
                                        <Terms />
                                    </div>
                                    <Buttons />
                                </ColumnBlock>
                            </SplitBlock>
                            {isProcessing && <DotsLoader isAbsolute />}
                        </Container>
                        <Editor />
                        <CopilotSidebar />
                    </CopilotContextManager>
                </CreationToolsUploadManager>
            </PortalAlertContext>
        </PortalMessageContextManager>
    )
}

export default CreationToolsUpload
